import React, { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Medal, TrendingUp, TrendingDown } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const tooltipStyle = { background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 };
const rankColor = (r) => r === 1 ? "text-[#C9A24B]" : r === 2 ? "text-slate-300" : r === 3 ? "text-amber-700" : "text-slate-500";

export default function Scorecard() {
  const { filters } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState(null);
  const [trendName, setTrendName] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = { start: filters.start, end: filters.end };
    if (filters.branch !== "all") params.branch = filters.branch;
    api.get("/scorecard/ranking", { params }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, [filters]);

  const openTrend = async (r) => {
    setTrendName(r.name);
    const { data } = await api.get("/scorecard/trend", { params: { technician_id: r.technician_id, months: 6 } });
    setTrend(data);
  };

  const overallColor = (v) => v >= 90 ? "text-emerald-400" : v >= 75 ? "text-[#C9A24B]" : "text-[#DC2626]";

  return (
    <div className="animate-fade-up" data-testid="scorecard-page">
      <PageHeader title="Technician Scorecard & Ranking" subtitle="Weighted across all 7 pillars → Overall Readiness %. Click a row for the 6-month trend." />

      {loading ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No performance data in range." /> : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                  {["Rank", "Technician", "Branch", "Role", "Overall", "Strongest Pillar", "Needs Attention", "Proc.", "Dosing", "Cross-sell"].map((h) => <th key={h} className="py-3 px-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.technician_id} onClick={() => openTrend(r)} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`rank-row-${r.technician_id}`}>
                    <td className="px-3 py-3"><span className={`flex items-center gap-1 font-bold ${rankColor(r.rank)}`}>{r.rank <= 3 && <Medal size={16} />}#{r.rank}</span></td>
                    <td className="px-3 font-medium">{r.name}</td>
                    <td className="px-3 text-slate-400">{r.branch}</td>
                    <td className="px-3 text-slate-400 text-xs">{r.role}</td>
                    <td className={`px-3 font-bold ${overallColor(r.overall)}`}>{r.overall}%</td>
                    <td className="px-3"><span className="flex items-center gap-1 text-emerald-400 text-xs"><TrendingUp size={13} />{r.strongest}</span></td>
                    <td className="px-3"><span className="flex items-center gap-1 text-[#DC2626] text-xs"><TrendingDown size={13} />{r.needs_attention}</span></td>
                    <td className="px-3">{r.procedures}</td>
                    <td className={`px-3 font-semibold ${r.dosing_errors > 0 ? "text-[#DC2626]" : "text-emerald-400"}`}>{r.dosing_errors}</td>
                    <td className="px-3 text-[#C9A24B]">{fmt(r.upsell_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!trend} onOpenChange={(o) => !o && setTrend(null)}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trophy className="text-[#C9A24B]" size={18} /> {trendName} — 6-Month Trend</DialogTitle></DialogHeader>
          {trend && (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="sop" name="SOP %" stroke="#a78bfa" strokeWidth={2} />
                <Line type="monotone" dataKey="result_quality" name="Result Quality %" stroke="#059669" strokeWidth={2} />
                <Line type="monotone" dataKey="attendance" name="Attendance %" stroke="#38bdf8" strokeWidth={2} />
                <Line type="monotone" dataKey="dosing_errors" name="Dosing Errors" stroke="#DC2626" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
